import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Trash2,
  Save,
  Plus,
  Eye,
  EyeOff,
  UploadCloud,
  FileText,
  Layout,
  Sparkles,
  X as XIcon,
} from "lucide-react";
import { Header, Footer, Loading } from "../components/Shell";
import { api } from "../lib/api";
import { AdminSideExport as AdminSide } from "./Admin";
import { useSeo } from "../lib/seo";

// -------------------------------------------------------------------------
// Block registry (used by both editor + renderer)
// -------------------------------------------------------------------------
export const PAGE_BLOCKS = [
  { type: "hero", label: "Hero", defaults: { title: "Judul besar", subtitle: "Deskripsi singkat", cta_label: "Mulai", cta_href: "/register", image: "" } },
  { type: "text", label: "Teks / Paragraf", defaults: { text: "Tulis konten di sini..." } },
  { type: "image", label: "Gambar", defaults: { url: "", caption: "", alt: "" } },
  { type: "video", label: "Video YouTube", defaults: { youtube_url: "" } },
  { type: "cta", label: "Ajakan (CTA)", defaults: { title: "Siap memulai?", description: "Daftar sekarang.", button_label: "Daftar", button_href: "/register" } },
  { type: "feature_grid", label: "Grid Fitur", defaults: { title: "Kenapa MASTOFA?", items: [{ title: "Praktis", desc: "Langsung dipakai." }, { title: "Terbukti", desc: "Studi kasus nyata." }, { title: "Support", desc: "Bantuan aktif." }] } },
  { type: "faq", label: "FAQ", defaults: { title: "Pertanyaan populer", items: [{ q: "Berapa lama akses?", a: "Selamanya." }] } },
  { type: "testimonial", label: "Testimoni", defaults: { title: "Kata mereka", items: [{ name: "Rina", role: "Seller Shopee", quote: "ROAS naik 2x." }] } },
  { type: "divider", label: "Pemisah", defaults: {} },
];

// Pre-built page templates
export const PAGE_TEMPLATES = [
  {
    key: "blank",
    label: "Halaman kosong",
    description: "Mulai dari nol, tambah block satu-satu.",
    blocks: [],
  },
  {
    key: "landing_product",
    label: "Landing Produk",
    description: "Hero + fitur + testimoni + FAQ + CTA. Cocok untuk jualan course/produk digital.",
    blocks: [
      { type: "hero", data: { title: "Transformasi Bisnis Anda dalam 30 Hari", subtitle: "Strategi digital marketing praktis yang sudah terbukti untuk ratusan seller Indonesia.", cta_label: "Ambil sekarang", cta_href: "/register", image: "" } },
      { type: "feature_grid", data: { title: "Yang Anda dapatkan", items: [
        { title: "Materi Video HD", desc: "Belajar dari studio profesional, subtitle Bahasa Indonesia." },
        { title: "Template Siap Pakai", desc: "Copy-paste script, funnel, dan campaign yang teruji." },
        { title: "Community VIP", desc: "Grup Telegram eksklusif untuk tanya jawab langsung." },
        { title: "Sertifikat Kelulusan", desc: "Bukti resmi kompetensi digital marketing Anda." },
      ] } },
      { type: "testimonial", data: { title: "Kata mereka yang sudah ikut", items: [
        { name: "Rina S.", role: "Seller Shopee", quote: "ROAS naik dari 1.8 ke 4.2 dalam 3 minggu. Materinya to the point." },
        { name: "Budi P.", role: "Digital Agency", quote: "Framework campaign-nya sudah saya pakai untuk klien. Cuan konsisten." },
      ] } },
      { type: "faq", data: { title: "Pertanyaan yang sering ditanya", items: [
        { q: "Berapa lama akses?", a: "Selamanya. Sekali bayar, akses seumur hidup + semua update ke depan." },
        { q: "Apakah cocok untuk pemula?", a: "Ya. Materi dirancang dari dasar sampai advanced." },
        { q: "Ada bantuan kalau saya bingung?", a: "Ada. Grup Telegram support aktif 7 hari seminggu." },
      ] } },
      { type: "cta", data: { title: "Siap memulai?", description: "Bergabung sekarang sebelum harga naik.", button_label: "Daftar sekarang", button_href: "/register" } },
    ],
  },
  {
    key: "promo_discount",
    label: "Promo Diskon",
    description: "Hero urgency + benefit + testimoni + CTA. Cocok untuk flash sale.",
    blocks: [
      { type: "hero", data: { title: "FLASH SALE 30% — Hanya 48 Jam", subtitle: "Kesempatan terbatas untuk dapat kelas premium dengan harga terbaik tahun ini.", cta_label: "Klaim diskon", cta_href: "/register", image: "" } },
      { type: "text", data: { text: "Kenapa diskon ini spesial?\n\nKami buka slot terbatas 100 seat untuk merayakan milestone 10.000 alumni. Setelah 48 jam, harga kembali normal — no kidding, no extension.\n\nAmbil sekarang selagi masih ada slot." } },
      { type: "feature_grid", data: { title: "Bonus khusus periode ini", items: [
        { title: "Bonus 1: Template Landing Page", desc: "5 template convert-tinggi siap pakai." },
        { title: "Bonus 2: Konsultasi 30 menit", desc: "1-on-1 dengan mentor untuk strategi Anda." },
        { title: "Bonus 3: Group VIP Selamanya", desc: "Akses grup Telegram premium tanpa batas waktu." },
      ] } },
      { type: "testimonial", data: { title: "Testimoni alumni", items: [
        { name: "Dini R.", role: "UMKM Owner", quote: "Diskon dulu, hasil belakangan? Justru dua-duanya dapet. Materinya premium." },
      ] } },
      { type: "cta", data: { title: "Tinggal 48 jam", description: "Setelah ini harga kembali normal. Jangan tunggu penyesalan.", button_label: "Ya, saya mau ambil", button_href: "/register" } },
    ],
  },
  {
    key: "about",
    label: "Halaman About",
    description: "Cerita brand + tim + values + kontak.",
    blocks: [
      { type: "hero", data: { title: "Kami membantu bisnis Indonesia tumbuh via digital marketing", subtitle: "Sejak 2019, MASTOFA sudah membantu 10.000+ seller dan agency untuk scale campaign dengan data-driven.", cta_label: "Lihat kelas kami", cta_href: "/courses", image: "" } },
      { type: "text", data: { text: "Cerita kami\n\nMASTOFA lahir dari kegelisahan: terlalu banyak course digital marketing yang hanya jual teori tanpa hasil nyata. Kami hadir dengan pendekatan lain — pembelajaran berbasis studi kasus riil, praktik langsung, dan support komunitas yang aktif.\n\nMisi kami adalah membuka akses ilmu digital marketing level agency untuk seller UMKM Indonesia." } },
      { type: "feature_grid", data: { title: "Nilai yang kami pegang", items: [
        { title: "Data over opinion", desc: "Semua strategi kami based on data campaign nyata, bukan asumsi." },
        { title: "Praktis over teori", desc: "Belajar sambil praktik langsung dengan account beriklan sungguhan." },
        { title: "Long-term over cepat", desc: "Tidak ada shortcut. Kami bangun fondasi yang bertahan." },
      ] } },
      { type: "cta", data: { title: "Punya pertanyaan?", description: "Tim support kami siap membantu setiap hari.", button_label: "Hubungi kami", button_href: "/contact" } },
    ],
  },
];

const newBlock = (type) => {
  const meta = PAGE_BLOCKS.find((b) => b.type === type);
  return {
    id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    data: JSON.parse(JSON.stringify(meta?.defaults || {})),
  };
};

const templateBlocks = (key) => {
  const t = PAGE_TEMPLATES.find((x) => x.key === key);
  if (!t) return [];
  return t.blocks.map((b) => ({
    id: `tpl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type: b.type,
    data: JSON.parse(JSON.stringify(b.data)),
  }));
};

// -------------------------------------------------------------------------
// Admin: list of pages
// -------------------------------------------------------------------------
export function AdminPages() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", slug: "", template: "blank" });
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const load = () => {
    setBusy(true);
    api("/admin/pages")
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setBusy(false));
  };
  useEffect(load, []);
  const create = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const blocks = templateBlocks(form.template);
      const r = await api("/admin/pages", {
        method: "POST",
        data: { title: form.title, slug: form.slug, blocks, status: "DRAFT" },
      });
      nav(`/admin/pages/${r.id}`);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Gagal membuat halaman.");
    }
  };
  const remove = async (id) => {
    if (!confirm("Hapus halaman ini? Aksi tidak bisa di-undo.")) return;
    await api(`/admin/pages/${id}`, { method: "DELETE" });
    load();
  };
  if (busy) return <Loading label="Memuat halaman..." />;
  return (
    <div className="dashboard">
      <AdminSide active="pages" />
      <main className="dash-main">
        <div className="dash-top">
          <div>
            <span className="section-kicker">PAGE CMS</span>
            <h1>Halaman kustom</h1>
            <p>Buat landing page, halaman tentang, promo, atau konten apa saja tanpa developer.</p>
          </div>
          <button
            className="button"
            onClick={() => setCreating(!creating)}
            data-testid="page-new-toggle"
          >
            <Plus size={16} /> Halaman baru
          </button>
        </div>
        {creating && (
          <form className="grant-box" onSubmit={create} data-testid="page-create-form">
            <b>Buat halaman baru</b>
            <div className="page-templates" data-testid="page-templates">
              {PAGE_TEMPLATES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={form.template === t.key ? "page-tpl active" : "page-tpl"}
                  onClick={() => setForm({ ...form, template: t.key })}
                  data-testid={`page-tpl-${t.key}`}
                >
                  <b>{t.label}</b>
                  <small>{t.description}</small>
                  <span className="page-tpl-count">
                    {t.blocks.length === 0 ? "Kosong" : `${t.blocks.length} block siap`}
                  </span>
                </button>
              ))}
            </div>
            <div className="grant-row">
              <input
                required
                placeholder="Judul halaman"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="page-new-title"
              />
              <input
                placeholder="Slug (opsional, di-generate dari judul)"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                data-testid="page-new-slug"
              />
              <button className="button" type="submit" data-testid="page-new-submit">
                Buat & Edit <ArrowRight size={15} />
              </button>
            </div>
            {err && <small className="assign-msg">{err}</small>}
            <small className="hint">
              URL: <code>/p/&lt;slug&gt;</code>. Slug reserved (admin, dashboard, courses, dll.) otomatis di-suffix.
            </small>
          </form>
        )}
        <div className="admin-table pages-admin-table" data-testid="pages-table">
          <div className="table">
            <div className="pages-row pages-row-head">
              <span>Judul</span>
              <span>Slug</span>
              <span>Status</span>
              <span>Views</span>
              <span>Diperbarui</span>
              <span>Aksi</span>
            </div>
            {items.length === 0 && (
              <div className="pages-row"><span className="empty-inline" style={{ gridColumn: "1 / -1" }}>Belum ada halaman kustom.</span></div>
            )}
            {items.map((p) => (
              <div className="pages-row" key={p.id} data-testid={`page-row-${p.slug}`}>
                <span>
                  <b>{p.title}</b>
                </span>
                <span>/p/{p.slug}</span>
                <span>
                  <span className={`pill ${p.status === "PUBLISHED" ? "pill-green" : "pill-muted"}`}>
                    {p.status}
                  </span>
                </span>
                <span>{p.views || 0}</span>
                <span>{new Date(p.updated_at).toLocaleDateString("id-ID")}</span>
                <span className="pages-row-actions">
                  <Link to={`/admin/pages/${p.id}`} className="button-ghost small" data-testid={`page-edit-${p.slug}`}>
                    Edit
                  </Link>
                  {p.status === "PUBLISHED" && (
                    <a
                      href={`/p/${p.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="button-ghost small"
                    >
                      <Eye size={13} /> Lihat
                    </a>
                  )}
                  <button
                    className="button-ghost small"
                    onClick={() => remove(p.id)}
                    data-testid={`page-delete-${p.slug}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </span>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

// -------------------------------------------------------------------------
// Admin: page editor (blocks add/reorder/edit/delete + SEO + publish)
// -------------------------------------------------------------------------
export function AdminPageEditor() {
  const { pageId } = useParams();
  const [page, setPage] = useState(null);
  const [msg, setMsg] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [tab, setTab] = useState("blocks"); // blocks | seo | settings
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiForm, setAiForm] = useState({ prompt: "", style: "sales" });
  const [aiErr, setAiErr] = useState("");
  useEffect(() => {
    api(`/admin/pages/${pageId}`).then(setPage).catch(() => setPage(null));
  }, [pageId]);
  const save = async (opts = {}) => {
    if (!page) return;
    try {
      const nextStatus = opts.publish ? "PUBLISHED" : opts.unpublish ? "DRAFT" : page.status;
      const saved = await api(`/admin/pages/${pageId}`, {
        method: "PATCH",
        data: {
          title: page.title,
          slug: page.slug,
          blocks: page.blocks,
          seo_title: page.seo_title || "",
          seo_description: page.seo_description || "",
          seo_image: page.seo_image || "",
          status: nextStatus,
          show_header: page.show_header !== false,
          show_footer: page.show_footer !== false,
        },
      });
      setPage(saved);
      setMsg(
        opts.publish ? "Halaman dipublish." : opts.unpublish ? "Halaman jadi draft." : "Tersimpan."
      );
      setTimeout(() => setMsg(""), 1800);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Gagal menyimpan.");
    }
  };
  const addBlock = (type) => {
    setPage({ ...page, blocks: [...(page.blocks || []), newBlock(type)] });
    setShowAdd(false);
  };
  const updateBlock = (i, patch) => {
    const next = [...page.blocks];
    next[i] = { ...next[i], data: { ...next[i].data, ...patch } };
    setPage({ ...page, blocks: next });
  };
  const moveBlock = (i, dir) => {
    const next = [...page.blocks];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setPage({ ...page, blocks: next });
  };
  const removeBlock = (i) => {
    if (!confirm("Hapus block ini?")) return;
    const next = [...page.blocks];
    next.splice(i, 1);
    setPage({ ...page, blocks: next });
  };
  const aiGenerate = async (mode) => {
    if (!aiForm.prompt.trim() || aiForm.prompt.trim().length < 8) {
      setAiErr("Prompt minimal 8 karakter.");
      return;
    }
    setAiBusy(true);
    setAiErr("");
    try {
      const r = await api("/admin/pages/ai-generate", {
        method: "POST",
        data: aiForm,
        timeout: 90000,
      });
      const newBlocks = r.blocks || [];
      setPage({
        ...page,
        blocks: mode === "replace" ? newBlocks : [...(page.blocks || []), ...newBlocks],
      });
      setAiOpen(false);
      setAiForm({ prompt: "", style: aiForm.style });
    } catch (e) {
      setAiErr(e.response?.data?.detail || "AI gagal membuat konten. Coba lagi.");
    } finally {
      setAiBusy(false);
    }
  };
  if (!page) return <Loading label="Memuat halaman..." />;
  return (
    <div className="dashboard">
      <AdminSide active="pages" />
      <main className="dash-main">
        <div className="dash-top">
          <div>
            <Link to="/admin/pages" className="back-link">← Halaman</Link>
            <h1 data-testid="page-editor-title">{page.title}</h1>
            <p>
              Status: <b>{page.status}</b> · Slug: /p/{page.slug}
              {page.status === "PUBLISHED" && (
                <>
                  {" "}·{" "}
                  <a href={`/p/${page.slug}`} target="_blank" rel="noreferrer">
                    Lihat live <ArrowRight size={12} />
                  </a>
                </>
              )}
            </p>
          </div>
          <div className="editor-actions">
            <button className="button-ghost" onClick={() => save()} data-testid="page-save">
              <Save size={15} /> Simpan draft
            </button>
            {page.status !== "PUBLISHED" ? (
              <button className="button" onClick={() => save({ publish: true })} data-testid="page-publish">
                <UploadCloud size={15} /> Publish
              </button>
            ) : (
              <button className="button-ghost" onClick={() => save({ unpublish: true })} data-testid="page-unpublish">
                <EyeOff size={15} /> Unpublish
              </button>
            )}
          </div>
        </div>
        {msg && <div className="save-toast" data-testid="page-toast">{msg}</div>}
        <div className="page-tabs">
          <button
            className={tab === "blocks" ? "tab-btn active" : "tab-btn"}
            onClick={() => setTab("blocks")}
            data-testid="page-tab-blocks"
          >
            <Layout size={14} /> Blocks ({(page.blocks || []).length})
          </button>
          <button
            className={tab === "seo" ? "tab-btn active" : "tab-btn"}
            onClick={() => setTab("seo")}
            data-testid="page-tab-seo"
          >
            SEO
          </button>
          <button
            className={tab === "settings" ? "tab-btn active" : "tab-btn"}
            onClick={() => setTab("settings")}
            data-testid="page-tab-settings"
          >
            Setting
          </button>
        </div>
        {tab === "blocks" && (
          <div className="page-editor-grid">
            <div className="page-blocks-col">
              {(page.blocks || []).length === 0 && (
                <div className="empty-inline">
                  Belum ada block. Tambahkan block pertama Anda.
                </div>
              )}
              {(page.blocks || []).map((b, i) => (
                <div key={b.id} className="page-block-edit" data-testid={`page-block-${i}`}>
                  <div className="page-block-head">
                    <span>
                      <FileText size={12} /> {PAGE_BLOCKS.find((t) => t.type === b.type)?.label || b.type}
                    </span>
                    <div>
                      <button onClick={() => moveBlock(i, -1)} aria-label="Naik" data-testid={`page-block-up-${i}`}>
                        <ArrowUp size={13} />
                      </button>
                      <button onClick={() => moveBlock(i, 1)} aria-label="Turun" data-testid={`page-block-down-${i}`}>
                        <ArrowDown size={13} />
                      </button>
                      <button
                        onClick={() => removeBlock(i)}
                        aria-label="Hapus"
                        data-testid={`page-block-remove-${i}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                  <BlockEditForm block={b} onChange={(patch) => updateBlock(i, patch)} idx={i} />
                </div>
              ))}
              <div className="block-add">
                <button
                  className="chip"
                  onClick={() => setShowAdd(!showAdd)}
                  data-testid="page-add-block-toggle"
                >
                  <Plus size={12} /> Tambah block
                </button>
                <button
                  className="chip ai-chip"
                  onClick={() => { setAiOpen(true); setAiErr(""); }}
                  data-testid="page-ai-toggle"
                >
                  <Sparkles size={12} /> Isi dengan AI
                </button>
                {showAdd && (
                  <div className="page-add-menu" data-testid="page-add-menu">
                    {PAGE_BLOCKS.map((t) => (
                      <button
                        key={t.type}
                        className="chip"
                        onClick={() => addBlock(t.type)}
                        data-testid={`page-add-${t.type}`}
                      >
                        <Plus size={11} /> {t.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <aside className="page-preview-col">
              <b>Preview live blocks</b>
              <div className="page-mini-preview">
                <PageRenderer blocks={page.blocks || []} compact />
              </div>
            </aside>
          </div>
        )}
        {tab === "seo" && (
          <div className="page-form">
            <label>
              Judul (title)
              <input
                value={page.title}
                onChange={(e) => setPage({ ...page, title: e.target.value })}
                data-testid="page-title-input"
              />
            </label>
            <label>
              Slug
              <input
                value={page.slug}
                onChange={(e) => setPage({ ...page, slug: e.target.value })}
                data-testid="page-slug-input"
              />
              <small className="hint">URL: /p/{page.slug}</small>
            </label>
            <label>
              SEO title (opsional, muncul di tab browser & Google)
              <input
                value={page.seo_title || ""}
                onChange={(e) => setPage({ ...page, seo_title: e.target.value })}
                data-testid="page-seo-title-input"
              />
            </label>
            <label>
              SEO description (155 karakter, muncul di snippet Google)
              <textarea
                rows="3"
                maxLength="200"
                value={page.seo_description || ""}
                onChange={(e) => setPage({ ...page, seo_description: e.target.value })}
                data-testid="page-seo-desc-input"
              />
            </label>
            <label>
              Gambar OG (URL, tampil saat di-share ke sosmed)
              <input
                value={page.seo_image || ""}
                onChange={(e) => setPage({ ...page, seo_image: e.target.value })}
                data-testid="page-seo-image-input"
              />
            </label>
          </div>
        )}
        {tab === "settings" && (
          <div className="page-form">
            <label>
              <input
                type="checkbox"
                checked={page.show_header !== false}
                onChange={(e) => setPage({ ...page, show_header: e.target.checked })}
                data-testid="page-show-header"
              />{" "}
              Tampilkan header MASTOFA (nav atas)
            </label>
            <label>
              <input
                type="checkbox"
                checked={page.show_footer !== false}
                onChange={(e) => setPage({ ...page, show_footer: e.target.checked })}
                data-testid="page-show-footer"
              />{" "}
              Tampilkan footer MASTOFA
            </label>
          </div>
        )}
        {aiOpen && (
          <div className="ai-modal-backdrop" onClick={() => !aiBusy && setAiOpen(false)}>
            <div
              className="ai-modal"
              onClick={(e) => e.stopPropagation()}
              data-testid="ai-page-modal"
            >
              <div className="ai-modal-head">
                <div>
                  <Sparkles size={18} />
                  <h3>Isi halaman dengan AI</h3>
                </div>
                <button
                  onClick={() => setAiOpen(false)}
                  disabled={aiBusy}
                  aria-label="Tutup"
                  data-testid="ai-modal-close"
                >
                  <XIcon size={18} />
                </button>
              </div>
              <p className="ai-modal-hint">
                Jelaskan ide halaman Anda dalam Bahasa Indonesia — kami generate hero, copy,
                testimoni, FAQ, dan CTA lengkap dalam hitungan detik. Powered by gpt-5.2.
              </p>
              <label>
                Gaya halaman
                <div className="ai-style-tabs">
                  {[
                    { k: "sales", l: "Jualan / Konversi" },
                    { k: "promo", l: "Promo Urgent" },
                    { k: "about", l: "Tentang Brand" },
                  ].map((s) => (
                    <button
                      key={s.k}
                      type="button"
                      className={aiForm.style === s.k ? "chip active" : "chip"}
                      onClick={() => setAiForm({ ...aiForm, style: s.k })}
                      data-testid={`ai-style-${s.k}`}
                    >
                      {s.l}
                    </button>
                  ))}
                </div>
              </label>
              <label>
                Brief singkat (contoh: &ldquo;Landing page kelas Meta Ads pemula, harga Rp 297.000, targeting seller Shopee&rdquo;)
                <textarea
                  rows="5"
                  maxLength="1000"
                  autoFocus
                  value={aiForm.prompt}
                  onChange={(e) => setAiForm({ ...aiForm, prompt: e.target.value })}
                  placeholder="Landing untuk kursus digital marketing untuk pemula, targeting seller Shopee, harga terjangkau, benefit utama..."
                  data-testid="ai-prompt-input"
                />
                <small className="hint">
                  {aiForm.prompt.length}/1000 karakter · minimal 8 karakter
                </small>
              </label>
              {aiErr && <div className="error" data-testid="ai-err">{aiErr}</div>}
              <div className="ai-modal-actions">
                <button
                  className="button"
                  onClick={() => aiGenerate("append")}
                  disabled={aiBusy || aiForm.prompt.trim().length < 8}
                  data-testid="ai-generate-append"
                >
                  {aiBusy ? (
                    <>
                      <span className="mini-spinner" /> Sedang generate...
                    </>
                  ) : (
                    <>
                      <Sparkles size={15} /> Generate & tambahkan
                    </>
                  )}
                </button>
                {(page.blocks || []).length > 0 && (
                  <button
                    className="button-ghost"
                    onClick={() => {
                      if (confirm("Ganti SEMUA block yang ada dengan hasil AI? Aksi tidak bisa di-undo.")) {
                        aiGenerate("replace");
                      }
                    }}
                    disabled={aiBusy || aiForm.prompt.trim().length < 8}
                    data-testid="ai-generate-replace"
                  >
                    Ganti semua block
                  </button>
                )}
              </div>
              <small className="hint" style={{ marginTop: 10, display: "block" }}>
                Setelah generate, Anda tetap bisa edit tiap block secara manual.
              </small>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// -------------------------------------------------------------------------
// Block edit forms (per type)
// -------------------------------------------------------------------------
function BlockEditForm({ block, onChange, idx }) {
  const t = block.type;
  const d = block.data || {};
  if (t === "hero") {
    return (
      <>
        <input placeholder="Judul besar" value={d.title || ""} onChange={(e) => onChange({ title: e.target.value })} data-testid={`page-hero-title-${idx}`} />
        <textarea rows="2" placeholder="Subtitle" value={d.subtitle || ""} onChange={(e) => onChange({ subtitle: e.target.value })} />
        <input placeholder="Label tombol CTA" value={d.cta_label || ""} onChange={(e) => onChange({ cta_label: e.target.value })} />
        <input placeholder="Link tombol CTA (contoh: /register)" value={d.cta_href || ""} onChange={(e) => onChange({ cta_href: e.target.value })} />
        <input placeholder="URL gambar background (opsional)" value={d.image || ""} onChange={(e) => onChange({ image: e.target.value })} />
      </>
    );
  }
  if (t === "text") {
    return (
      <textarea
        rows="6"
        placeholder="Tulis konten... (baris baru = paragraf baru)"
        value={d.text || ""}
        onChange={(e) => onChange({ text: e.target.value })}
        data-testid={`page-text-${idx}`}
      />
    );
  }
  if (t === "image") {
    return (
      <>
        <input placeholder="URL gambar" value={d.url || ""} onChange={(e) => onChange({ url: e.target.value })} data-testid={`page-image-url-${idx}`} />
        <input placeholder="Alt text (untuk SEO & aksesibilitas)" value={d.alt || ""} onChange={(e) => onChange({ alt: e.target.value })} />
        <input placeholder="Caption (opsional)" value={d.caption || ""} onChange={(e) => onChange({ caption: e.target.value })} />
      </>
    );
  }
  if (t === "video") {
    return (
      <input
        placeholder="URL YouTube (https://youtube.com/watch?v=...)"
        value={d.youtube_url || ""}
        onChange={(e) => onChange({ youtube_url: e.target.value })}
        data-testid={`page-video-url-${idx}`}
      />
    );
  }
  if (t === "cta") {
    return (
      <>
        <input placeholder="Judul CTA" value={d.title || ""} onChange={(e) => onChange({ title: e.target.value })} />
        <textarea rows="2" placeholder="Deskripsi" value={d.description || ""} onChange={(e) => onChange({ description: e.target.value })} />
        <input placeholder="Label tombol" value={d.button_label || ""} onChange={(e) => onChange({ button_label: e.target.value })} />
        <input placeholder="Link tombol" value={d.button_href || ""} onChange={(e) => onChange({ button_href: e.target.value })} />
      </>
    );
  }
  if (t === "feature_grid") {
    const items = d.items || [];
    return (
      <>
        <input placeholder="Judul section" value={d.title || ""} onChange={(e) => onChange({ title: e.target.value })} />
        {items.map((it, i) => (
          <div key={i} className="page-item-row">
            <input placeholder="Judul item" value={it.title} onChange={(e) => {
              const next = [...items]; next[i] = { ...it, title: e.target.value }; onChange({ items: next });
            }} />
            <input placeholder="Deskripsi item" value={it.desc} onChange={(e) => {
              const next = [...items]; next[i] = { ...it, desc: e.target.value }; onChange({ items: next });
            }} />
            <button onClick={() => { const next = [...items]; next.splice(i, 1); onChange({ items: next }); }} aria-label="Hapus item"><Trash2 size={11} /></button>
          </div>
        ))}
        <button className="chip" onClick={() => onChange({ items: [...items, { title: "", desc: "" }] })}>
          <Plus size={11} /> Tambah item
        </button>
      </>
    );
  }
  if (t === "faq") {
    const items = d.items || [];
    return (
      <>
        <input placeholder="Judul section" value={d.title || ""} onChange={(e) => onChange({ title: e.target.value })} />
        {items.map((it, i) => (
          <div key={i} className="page-item-row">
            <input placeholder="Pertanyaan" value={it.q} onChange={(e) => {
              const next = [...items]; next[i] = { ...it, q: e.target.value }; onChange({ items: next });
            }} />
            <textarea rows="2" placeholder="Jawaban" value={it.a} onChange={(e) => {
              const next = [...items]; next[i] = { ...it, a: e.target.value }; onChange({ items: next });
            }} />
            <button onClick={() => { const next = [...items]; next.splice(i, 1); onChange({ items: next }); }} aria-label="Hapus"><Trash2 size={11} /></button>
          </div>
        ))}
        <button className="chip" onClick={() => onChange({ items: [...items, { q: "", a: "" }] })}>
          <Plus size={11} /> Tambah FAQ
        </button>
      </>
    );
  }
  if (t === "testimonial") {
    const items = d.items || [];
    return (
      <>
        <input placeholder="Judul section" value={d.title || ""} onChange={(e) => onChange({ title: e.target.value })} />
        {items.map((it, i) => (
          <div key={i} className="page-item-row">
            <input placeholder="Nama" value={it.name} onChange={(e) => {
              const next = [...items]; next[i] = { ...it, name: e.target.value }; onChange({ items: next });
            }} />
            <input placeholder="Peran (contoh: Seller Shopee)" value={it.role} onChange={(e) => {
              const next = [...items]; next[i] = { ...it, role: e.target.value }; onChange({ items: next });
            }} />
            <textarea rows="2" placeholder="Testimoni" value={it.quote} onChange={(e) => {
              const next = [...items]; next[i] = { ...it, quote: e.target.value }; onChange({ items: next });
            }} />
            <button onClick={() => { const next = [...items]; next.splice(i, 1); onChange({ items: next }); }} aria-label="Hapus"><Trash2 size={11} /></button>
          </div>
        ))}
        <button className="chip" onClick={() => onChange({ items: [...items, { name: "", role: "", quote: "" }] })}>
          <Plus size={11} /> Tambah testimoni
        </button>
      </>
    );
  }
  if (t === "divider") {
    return <small className="hint">Pemisah horizontal (tidak ada setting).</small>;
  }
  return <small className="hint">Block tipe {t} belum didukung.</small>;
}

// -------------------------------------------------------------------------
// Public renderer
// -------------------------------------------------------------------------
export function PublicPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api(`/pages/${slug}`)
      .then(setPage)
      .catch((e) => setErr(e.response?.data?.detail || "Halaman tidak ditemukan."));
  }, [slug]);
  useSeo({
    title: page ? (page.seo_title || page.title) : "Halaman",
    description: page ? (page.seo_description || page.title) : "",
    path: `/p/${slug}`,
    image: page?.seo_image,
    type: "article",
  });
  if (err) {
    return (
      <div>
        <Header />
        <main className="page"><p>{err}</p><Link className="button" to="/">Kembali ke beranda</Link></main>
        <Footer />
      </div>
    );
  }
  if (!page) return <Loading label="Memuat halaman..." />;
  return (
    <div>
      {page.show_header !== false && <Header />}
      <main className="page-cms-render" data-testid="public-page-render">
        <PageRenderer blocks={page.blocks || []} />
      </main>
      {page.show_footer !== false && <Footer />}
    </div>
  );
}

// -------------------------------------------------------------------------
// Renderer (used by public page + admin live preview)
// -------------------------------------------------------------------------
export function PageRenderer({ blocks, compact = false }) {
  return (
    <div className={compact ? "page-render compact" : "page-render"}>
      {blocks.map((b) => (
        <RenderBlock key={b.id} block={b} />
      ))}
    </div>
  );
}

function RenderBlock({ block }) {
  const d = block.data || {};
  const t = block.type;
  if (t === "hero") {
    return (
      <section
        className="pg-hero"
        data-testid={`render-hero-${block.id}`}
        style={
          d.image
            ? { backgroundImage: `linear-gradient(rgba(15,23,42,.55),rgba(15,23,42,.55)), url(${d.image})`, backgroundSize: "cover", backgroundPosition: "center", color: "#fff" }
            : {}
        }
      >
        <h1>{d.title}</h1>
        {d.subtitle && <p>{d.subtitle}</p>}
        {d.cta_label && d.cta_href && (
          <Link to={d.cta_href} className="button">
            {d.cta_label} <ArrowRight size={16} />
          </Link>
        )}
      </section>
    );
  }
  if (t === "text") {
    return (
      <section className="pg-text" data-testid={`render-text-${block.id}`}>
        {(d.text || "").split(/\n\n+/).map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </section>
    );
  }
  if (t === "image") {
    if (!d.url) return null;
    return (
      <figure className="pg-image" data-testid={`render-image-${block.id}`}>
        <img src={d.url} alt={d.alt || d.caption || ""} loading="lazy" />
        {d.caption && <figcaption>{d.caption}</figcaption>}
      </figure>
    );
  }
  if (t === "video") {
    const yt = (d.youtube_url || "").match(/(?:v=|\/shorts\/|youtu\.be\/)([\w-]{6,})/)?.[1];
    if (!yt) return null;
    return (
      <section className="pg-video" data-testid={`render-video-${block.id}`}>
        <div className="block-video">
          <iframe src={`https://www.youtube.com/embed/${yt}`} title="Video" allowFullScreen />
        </div>
      </section>
    );
  }
  if (t === "cta") {
    return (
      <section className="pg-cta" data-testid={`render-cta-${block.id}`}>
        <h2>{d.title}</h2>
        {d.description && <p>{d.description}</p>}
        {d.button_label && d.button_href && (
          <Link to={d.button_href} className="button">
            {d.button_label} <ArrowRight size={16} />
          </Link>
        )}
      </section>
    );
  }
  if (t === "feature_grid") {
    return (
      <section className="pg-features" data-testid={`render-features-${block.id}`}>
        {d.title && <h2>{d.title}</h2>}
        <div className="pg-features-grid">
          {(d.items || []).map((it, i) => (
            <div key={i} className="pg-feature">
              <b>{it.title}</b>
              <p>{it.desc}</p>
            </div>
          ))}
        </div>
      </section>
    );
  }
  if (t === "faq") {
    return (
      <section className="pg-faq" data-testid={`render-faq-${block.id}`}>
        {d.title && <h2>{d.title}</h2>}
        <div className="pg-faq-list">
          {(d.items || []).map((it, i) => (
            <details key={i}>
              <summary>{it.q}</summary>
              <p>{it.a}</p>
            </details>
          ))}
        </div>
      </section>
    );
  }
  if (t === "testimonial") {
    return (
      <section className="pg-testi" data-testid={`render-testi-${block.id}`}>
        {d.title && <h2>{d.title}</h2>}
        <div className="pg-testi-grid">
          {(d.items || []).map((it, i) => (
            <blockquote key={i}>
              <p>&ldquo;{it.quote}&rdquo;</p>
              <cite>
                <b>{it.name}</b>
                {it.role && <span> · {it.role}</span>}
              </cite>
            </blockquote>
          ))}
        </div>
      </section>
    );
  }
  if (t === "divider") {
    return <hr className="pg-divider" />;
  }
  return null;
}
